import { useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  CustomTableCell,
  TableHead,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
  AutoCompleteWrapper,
  ChartWithNoteWrapper,
  TableHeadWithoutOrdenation,
} from '../../components'
import {
  InputDataQualityProvider,
  useInputDataQuality,
} from '../../contexts/InputDataQualityContext'
import useViewport from '../../hooks/useViewport'
import { getComparator } from '../../utils/graph'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: #f1f3f5;
`

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px 44px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: '100%';
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`
const getLastDaysHeaders = (days) => {
  const headers = [];
  const today = new Date();

  for (let i = 0; i < days; i++) {
    const date = new Date();
    date.setDate(today.getDate() - i);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const formattedDate = `${day}/${month}`;

    headers.push({
      id: formattedDate,
      numeric: false,
      disablePadding: false,
      label: formattedDate,
      align: 'left',
    });
  }

  return headers.reverse();
}

const rowsPerPage = 10

const ReportUserDataQualityPage = () => {
  const [order, setOrder] = useState('asc')
  const [page, setPage] = useState(0)
  const { width } = useViewport()
  const { t } = useTranslation('common')

  const headState = [
    {
      id: 'Nome',
      numeric: false,
      disablePadding: false,
      label: t('reports.user_data.name'),
      align: 'left',
    },
    ...getLastDaysHeaders(14)
  ]
  const [orderBy, setOrderBy] = useState(headState[0].id)

  const {
    rows,
    dataMessage,
    loading: loadingData,
    loadingFilters,
    filterOptions,
    onSelectFilter,
    selectedFilterOptions,
    selectedUser,
    onSelectUser,
    inputValue,
    onInputValueChange,
  } = useInputDataQuality()
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const emptyRows = () => {
    return page > 0 || rows.length < 10
      ? Math.max(0, (1 + page) * rowsPerPage - rows.length)
      : 0
  }

  const visibleRows = useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  )

  const handleButtonClick = () => {
  }

  return (
    <Container>
      <ContentContainer>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {t('reports.user_data.title')}
            </span>
            <Tooltip
              title={t('reports.user_data.tooltip')}
              placement='right'
              arrow
            >
              <HelpOutline fontSize='18' />
            </Tooltip>
          </div>
        </Header>
        <BodyContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 16,
                  }}
                >
                  <Filter onClick={() => setOpenFilterDrawer(true)} />
                </div>
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <GroupedFilterTags
                    tags={selectedFilterOptionsList({
                      selectedFilterOptions,
                    })}
                    onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                    maxTagsToShow={getMaxTagsToShow({ width })}
                  />
                </div>
              )}
          </FiltersTagsContainer>
          <TableContainer>
            <ChartWithNoteWrapper
              customMessage={
                <>
                  {t('reports.user_data.custom_message.one')} <b>{dataMessage?.total_users} {t('users')}</b>{' '}
                  {t('totaling')} <b>{dataMessage?.percentage_done}%</b> {t('reports.user_data.custom_message.two')}
                </>
              }
            >
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby='notificationsTable'
              >
                <TableHeadWithoutOrdenation
                  headCells={headState}
                />
                <TableBody>
                  {!loadingData ? (
                    <>
                      {rows.length > 0 ? (
                        <>
                          {visibleRows.map(row => {
                            return (
                              <TableRow
                                hover
                                tabIndex={-1}
                                key={row.name}
                              >
                                <CustomTableCell
                                  data={row.name}
                                  type='text'
                                  sx={{ cursor: 'pointer' }}
                                />
                                <CustomTableCell type='action' data={row.data[0][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[1][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[2][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[3][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[4][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[5][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[6][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[7][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[8][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[9][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[10][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[11][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[12][1] ? 'yes' : 'no'} />
                                <CustomTableCell type='action' data={row.data[13][1] ? 'yes' : 'no'} />
                              </TableRow>
                            )
                          })}
                          {emptyRows() > 0 && (
                            <TableRow
                              style={{
                                height: 53 * emptyRows(),
                              }}
                            >
                              <TableCell colSpan={5} />
                            </TableRow>
                          )}
                        </>
                      ) : (
                        <TableRow style={{ height: 53 * 10 }}>
                          <TableCell
                            colSpan={5}
                            align='center'
                          >
                            {t('no_data')}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ) : (
                    <TableRow style={{ height: 53 * 10 }}>
                      <TableCell
                        colSpan={5}
                        align='center'
                      >
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ChartWithNoteWrapper>
          </TableContainer>
          <TablePagination
            component='div'
            count={rows.length}
            showFirstButton
            showLastButton
            labelRowsPerPage={''}
            rowsPerPageOptions={[]}
            rowsPerPage={10}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ to, count }) =>
              {t('reports.user_data.footer.colaborators', {to: to, count: count})}
            }
          />
        </BodyContainer>
      </ContentContainer>
      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
    </Container>
  )
}

const ReportUserDataQuality = () => {
  return (
    <InputDataQualityProvider>
      <ReportUserDataQualityPage />
    </InputDataQualityProvider>
  )
}

export default ReportUserDataQuality
